import React, { useState } from 'react';
import T from './theme';
import CollapsibleCard from './CollapsibleCard';
import { parsePace, formatPace, upcomingSchedule } from './utils';

export default function ScheduledRunsCard({
  schedule, myWorkouts, partners, trainingBands,
  planStarts, setPlanStarts,
  planMinutes, setPlanMinutes,
  planKind, setPlanKind,
  planTitle, setPlanTitle,
  onAddScheduled, onDeleteScheduled,
  onScheduleSavedWorkout, onDeleteWorkout,
  onSaveDraftWorkout, onScheduleDraftWorkout,
  onInviteEvent,
  setError,
}) {
  const [showWorkoutBuilder, setShowWorkoutBuilder] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftSteps, setDraftSteps] = useState([]);

  const paceFromBand = (name) => {
    const band = (trainingBands?.bands || []).find((b) => b.name === name);
    if (band?.pace_low && band?.pace_high) return `${band.pace_low}–${band.pace_high}`;
    return band?.pace_low || '';
  };

  const newDraftStep = (band = 'Easy') => ({
    key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    label: '',
    minutes: '10',
    band,
    paceInput: paceFromBand(band),
  });

  const openWorkoutBuilder = (seed) => {
    if (seed?.steps?.length) {
      setDraftName(seed.name || '');
      setDraftSteps(
        seed.steps.map((step, i) => ({
          key: `seed-${i}-${step.band || 'Easy'}`,
          label: step.label || '',
          minutes: String(step.duration_min || 10),
          band: step.band || 'Easy',
          paceInput: step.pace_target
            || (step.pace_low && step.pace_high ? `${step.pace_low}–${step.pace_high}` : '')
            || paceFromBand(step.band || 'Easy'),
        }))
      );
    } else {
      setDraftName('');
      setDraftSteps([newDraftStep('Easy')]);
    }
    setShowWorkoutBuilder(true);
  };

  const draftApiSteps = () => {
    const steps = [];
    for (const row of draftSteps) {
      const minutes = Number(row.minutes);
      if (!minutes || minutes < 1) continue;
      const step = {
        label: (row.label || '').trim() || null,
        duration_min: minutes,
        band: row.band || 'Easy',
      };
      const custom = parsePace(row.paceInput);
      if (custom) {
        step.pace_target_min = custom;
        step.pace_target = formatPace(custom);
      }
      steps.push(step);
    }
    return steps;
  };

  const handleSaveDraft = async () => {
    const name = draftName.trim();
    const steps = draftApiSteps();
    if (name.length < 2) {
      setError('Give the workout a name.');
      return;
    }
    if (!steps.length) {
      setError('Add at least one step with a duration.');
      return;
    }
    await onSaveDraftWorkout(name, steps);
    setShowWorkoutBuilder(false);
  };

  const handleScheduleDraft = async () => {
    const name = draftName.trim() || 'Custom workout';
    const steps = draftApiSteps();
    if (!steps.length) {
      setError('Add at least one step with a duration.');
      return;
    }
    await onScheduleDraftWorkout(name, steps);
    setShowWorkoutBuilder(false);
  };

  const linkButtonStyle = T.linkButtonStyle;

  return (
    <CollapsibleCard title="Scheduled runs" accentColor={T.color.accentSchedule}>
      <p style={{ color: T.color.textMuted, marginTop: 0 }}>
        The next run on this list is when you need to be recovered. Avoid scheduling a hard session before you'll be fully recovered.
        Define your own workout, schedule it, then invite a partner.
      </p>
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${T.color.borderLight}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <strong>My workouts</strong>
          <button type="button" onClick={() => openWorkoutBuilder()}>
            {showWorkoutBuilder ? 'Close builder' : 'Define a workout'}
          </button>
        </div>
        {showWorkoutBuilder && (
          <div style={{ margin: '8px 0 12px' }}>
            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Workout name (e.g. Tuesday cruise)"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
            />
            {draftSteps.map((row, i) => (
              <div key={row.key} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => {
                    const next = [...draftSteps];
                    next[i] = { ...row, label: e.target.value };
                    setDraftSteps(next);
                  }}
                  placeholder="Label"
                  style={{ width: 90 }}
                />
                <input
                  type="number"
                  min="1"
                  max="180"
                  value={row.minutes}
                  onChange={(e) => {
                    const next = [...draftSteps];
                    next[i] = { ...row, minutes: e.target.value };
                    setDraftSteps(next);
                  }}
                  style={{ width: 56 }}
                />
                <span style={{ fontSize: T.font.sm, color: T.color.textLight }}>min</span>
                <select
                  value={row.band}
                  onChange={(e) => {
                    const band = e.target.value;
                    const next = [...draftSteps];
                    next[i] = { ...row, band, paceInput: paceFromBand(band) };
                    setDraftSteps(next);
                  }}
                >
                  {['Easy', 'Marathon', 'Threshold', 'Interval', 'Repetition'].map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={row.paceInput}
                  onChange={(e) => {
                    const next = [...draftSteps];
                    next[i] = { ...row, paceInput: e.target.value };
                    setDraftSteps(next);
                  }}
                  placeholder="mm:ss or use default"
                  style={{ width: 110 }}
                />
                <button
                  type="button"
                  onClick={() => setDraftSteps(draftSteps.filter((_, j) => j !== i))}
                  style={linkButtonStyle}
                >
                  Remove
                </button>
              </div>
            ))}
            <p>
              <button type="button" onClick={() => setDraftSteps([...draftSteps, newDraftStep()])}>
                Add step
              </button>
              {' '}
              <button type="button" onClick={handleSaveDraft}>Save workout</button>
              {' '}
              <button type="button" onClick={handleScheduleDraft}>Save and schedule</button>
            </p>
            <span style={{ color: T.color.textLight, fontSize: T.font.sm }}>
              Selecting a band auto-fills your training paces. Type mm:ss to override. Save and schedule uses the date/time below.
            </span>
          </div>
        )}
        {myWorkouts.length === 0 && !showWorkoutBuilder ? (
          <p style={{ color: T.color.textLight, fontSize: T.font.sm, margin: 0 }}>
            No saved workouts yet. Define one, or save the recommended session.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {myWorkouts.map((workout) => (
              <li key={workout.id} style={{ marginBottom: 8 }}>
                <strong>{workout.name}</strong>
                {workout.duration_display ? ` · ${workout.duration_display}` : ''}
                {workout.kind ? ` · ${workout.kind}` : ''}
                <div>
                  <button type="button" onClick={() => onScheduleSavedWorkout(workout)} style={{ ...linkButtonStyle, marginRight: 12 }}>
                    Schedule
                  </button>
                  <button type="button" onClick={() => openWorkoutBuilder(workout)} style={{ ...linkButtonStyle, marginRight: 12 }}>
                    Duplicate
                  </button>
                  <button type="button" onClick={() => onDeleteWorkout(workout.id)} style={linkButtonStyle}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <form onSubmit={onAddScheduled} style={{ marginBottom: 12 }}>
        <input
          type="datetime-local"
          value={planStarts}
          onChange={(e) => setPlanStarts(e.target.value)}
          style={{ margin: '4px 8px 4px 0' }}
          required
        />
        <input
          type="number"
          min="1"
          max="480"
          value={planMinutes}
          onChange={(e) => setPlanMinutes(e.target.value)}
          placeholder="minutes"
          style={{ width: 80, margin: '4px 8px 4px 0' }}
        />
        <select value={planKind} onChange={(e) => setPlanKind(e.target.value)} style={{ margin: '4px 8px 4px 0' }}>
          <option value="easy">Easy</option>
          <option value="long">Long</option>
          <option value="workout">Workout</option>
        </select>
        <input
          type="text"
          value={planTitle}
          onChange={(e) => setPlanTitle(e.target.value)}
          placeholder="Title (optional)"
          style={{ margin: '4px 8px 4px 0' }}
        />
        <button type="submit" style={{ margin: '4px 0' }}>Add</button>
      </form>
      {upcomingSchedule(schedule).length === 0 ? (
        <p>No upcoming runs. Add a run and today's workout recommendation will adjust.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {upcomingSchedule(schedule).map((event) => (
            <li key={event.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${T.color.borderLight}` }}>
              <strong>{event.title}</strong>
              {' · '}
              {event.starts_at_local}
              {event.planned_minutes ? ` · ${event.planned_minutes} min ${event.kind}` : ''}
              {event.with && event.with.length
                ? ` · with ${event.with.map((p) => p.display_name).join(', ')}`
                : ''}
              {event.invited_by
                ? ` · added by ${event.invited_by.display_name} (their paces)`
                : ''}
              {event.prescription && event.prescription[0] && event.prescription[0].pace_target
                ? ` · ${event.prescription[0].pace_target}${
                    event.prescription[0].pace_low && event.prescription[0].pace_high
                      ? ` (${event.prescription[0].pace_low}–${event.prescription[0].pace_high})`
                      : ''
                  }`
                : ''}
              {event.predicted_recovery_h ? ` · ~${event.predicted_recovery_h} h recovery` : ''}
              {event.conflict && (
                <div style={{ color: T.color.danger }}>
                  Recovery from this would still be running at {event.conflict_with}. You may not be fully recovered by then.
                </div>
              )}
              <div>
                {!event.invited_by && partners.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onInviteEvent(event)}
                    style={{ ...linkButtonStyle, marginRight: 12 }}
                  >
                    Invite a partner
                  </button>
                )}
                <button type="button" onClick={() => onDeleteScheduled(event.id)} style={linkButtonStyle}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}
